import React, { useState } from 'react';
import axiosInstance from '../request';
import { URL } from '../api';
import getPrivilegeList from './PrivilegeList';

const EmiCalculator = () => {

  const privilege = getPrivilegeList();

  /* ===============================
     EXISTING EMI CALCULATOR STATES
  =============================== */

  const [formData, setFormData] = useState({
    loanAmount: "",
    interestRate: "",
    tenureDays: "",
    platformFee: "",
    collectionFee: "",
    convenienceFee: "",
    repaymentType: "1"
  });

  const [emiList, setEmiList] = useState([]);
  const [loading, setLoading] = useState(false);

  const [showModal, setShowModal] = useState(false);

  /* ===============================
     FINANCE EMI CALCULATOR STATES
  =============================== */

  const [financeForm, setFinanceForm] = useState({
    loanAmount: "",
    processingFeePercent: "",
    tenureMonths: ""
  });

  const [financeSummary, setFinanceSummary] = useState(null);
  const [financeEmiList, setFinanceEmiList] = useState([]);
  const [showFinanceModal, setShowFinanceModal] = useState(false);
  const [financeLoading, setFinanceLoading] = useState(false);

  /* ===============================
     HANDLE INPUT
  =============================== */

  const handleChange = (e) => {
    setFormData({...formData, [e.target.name]: e.target.value});
  };

  const handleFinanceChange = (e) => {
    setFinanceForm({...financeForm, [e.target.name]: e.target.value});
  };

  /* ===============================
     EXISTING EMI API
  =============================== */

  const calculateEmi = async () => {

    setLoading(true);
    setEmiList([]);

    try {

      const {
        loanAmount,
        interestRate,
        tenureDays,
        platformFee,
        collectionFee,
        convenienceFee,
        repaymentType
      } = formData;

      const response = await axiosInstance.get(
        `${URL.EMI_CALCULATE}?loanAmount=${loanAmount}&interestRate=${interestRate}&tenureDays=${tenureDays}&platformFee=${platformFee}&collectionFee=${collectionFee}&convenienceFee=${convenienceFee}&repaymentType=${repaymentType}`
      );

      setEmiList(response.data);
      setShowModal(true);

    } catch (error) {
      console.error("Error fetching EMI:", error);
    } finally {
      setLoading(false);
    }
  };

  /* ===============================
     FINANCE EMI API
  =============================== */

  const calculateFinanceEmi = async () => {

    setFinanceLoading(true);
    setFinanceEmiList([]);
    setFinanceSummary(null);

    try {

      const {
        loanAmount,
        processingFeePercent,
        tenureMonths
      } = financeForm;

      const response = await axiosInstance.post(
        URL.EMI_CLUCULATOR_FOR_FINANCE,
        null,
        {
          params: {
            loanAmount,
            tenureMonths,
            processingFeePercent
          }
        }
      );

      setFinanceSummary(response.data.summary);
      setFinanceEmiList(response.data.emiSchedule || []);
      setShowFinanceModal(true);

    } catch (error) {
      console.error("Finance EMI error:", error);
    } finally {
      setFinanceLoading(false);
    }
  };

  return (
    <div className="container mt-4">

      {/* ===============================
           EXISTING EMI CALCULATOR
      =============================== */}

      {privilege.emiCalculatorNormalView && (
        <>
          <h3 className="mb-4">EMI Calculator</h3>

          <div className="card p-3 shadow-sm mb-5">
            <div className="row">

              {[
                { label: "Loan Amount", name: "loanAmount" },
                { label: "Interest Rate (per day %)", name: "interestRate" },
                { label: "Tenure Days", name: "tenureDays" },
                { label: "Platform Fee %", name: "platformFee" },
                { label: "Collection Fee %", name: "collectionFee" },
                { label: "Convenience Fee %", name: "convenienceFee" }
              ].map((field, idx) => (
                <div className="col-md-4 mb-3" key={idx}>
                  <label>{field.label}</label>
                  <input
                    type="number"
                    className="form-control"
                    name={field.name}
                    value={formData[field.name]}
                    onChange={handleChange}
                  />
                </div>
              ))}

              <div className="col-md-4 mb-3">
                <label>Repayment Type</label>
                <select
                  className="form-control"
                  name="repaymentType"
                  value={formData.repaymentType}
                  onChange={handleChange}
                >
                  <option value="1">Daily EMI</option>
                  <option value="4">Single EMI</option>
                </select>
              </div>

            </div>

            <button className="btn btn-primary mt-2" onClick={calculateEmi}>
              Calculate EMI
            </button>

          </div>
        </>
      )}


      {/* ===============================
         NEW FINANCE EMI CALCULATOR
      =============================== */}

      {privilege.emiCalculatorFinanceView && (
        <>
          <h3 className="mb-4">EMI Calculator For Finance</h3>

          <div className="card p-3 shadow-sm mb-4">

            <div className="row">

              <div className="col-md-3 mb-3">
                <label>Loan Amount</label>
                <input
                  type="number"
                  className="form-control"
                  name="loanAmount"
                  value={financeForm.loanAmount}
                  onChange={handleFinanceChange}
                />
              </div>

              <div className="col-md-3 mb-3">
                <label>Processing Fee %</label>
                <input
                  type="number"
                  className="form-control"
                  name="processingFeePercent"
                  value={financeForm.processingFeePercent}
                  onChange={handleFinanceChange}
                />
              </div>

              <div className="col-md-3 mb-3">
                <label>Tenure Months</label>
                <input
                  type="number"
                  className="form-control"
                  name="tenureMonths"
                  value={financeForm.tenureMonths}
                  onChange={handleFinanceChange}
                />
              </div>

            </div>

            <button className="btn btn-success mt-2" onClick={calculateFinanceEmi}>
              Calculate Finance EMI
            </button>

          </div>
        </>
      )}


      {/* ===============================
          EXISTING EMI MODAL
      =============================== */}

      {showModal && (
        <div
          className="modal show fade"
          style={{ display: "block", background: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content">

              <div className="modal-header">
                <h5 className="modal-title">EMI Schedule</h5>
                <button className="btn-close" onClick={() => setShowModal(false)}></button>
              </div>

              <div className="modal-body" style={{ maxHeight: "70vh", overflowY: "auto" }}>

                <table className="table table-bordered table-hover">

                  <thead className="table-dark">
                    <tr>
                      <th>EMI No</th>
                      <th>Principal</th>
                      <th>Interest</th>
                      <th>Fees</th>
                      <th>EMI Amount</th>
                      <th>Outstanding</th>
                      <th>EMI Date</th>
                    </tr>
                  </thead>

                  <tbody>

                    {emiList.map((emi, index) => (
                      <tr key={index}>
                        <td>{emi.emiNumber}</td>
                        <td>₹{emi.principal}</td>
                        <td>₹{emi.interest}</td>
                        <td>₹{emi.fees}</td>
                        <td className="fw-bold">₹{emi.emiAmount}</td>
                        <td>₹{emi.outstandingAmount}</td>
                        <td>{emi.emiDate}</td>
                      </tr>
                    ))}

                  </tbody>

                </table>

              </div>

              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowModal(false)}
                >
                  Close
                </button>
              </div>

            </div>
          </div>
        </div>
      )}

      {/* ===============================
          FINANCE EMI MODAL
      =============================== */}

      {showFinanceModal && (
        <div
          className="modal show fade"
          style={{ display: "block", background: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-xl">
            <div className="modal-content">

              <div className="modal-header">
                <h5 className="modal-title">Finance EMI Schedule (Reducing Balance)</h5>
                <button className="btn-close" onClick={() => setShowFinanceModal(false)}></button>
              </div>

              <div className="modal-body" style={{ maxHeight: "75vh", overflowY: "auto" }}>

                {/* Summary Section */}
                {financeSummary && (
                  <div className="row mb-4">
                    <div className="col-md-3 mb-2">
                      <div className="card bg-light p-2 text-center">
                        <small className="text-muted">Principal</small>
                        <strong>₹{financeSummary.principal}</strong>
                      </div>
                    </div>
                    <div className="col-md-3 mb-2">
                      <div className="card bg-light p-2 text-center">
                        <small className="text-muted">Processing Fee</small>
                        <strong>₹{financeSummary.processingFee}</strong>
                      </div>
                    </div>
                    <div className="col-md-3 mb-2">
                      <div className="card bg-light p-2 text-center">
                        <small className="text-muted">GST</small>
                        <strong>₹{financeSummary.gst}</strong>
                      </div>
                    </div>
                    <div className="col-md-3 mb-2">
                      <div className="card bg-light p-2 text-center">
                        <small className="text-muted">Total Charges</small>
                        <strong>₹{financeSummary.totalCharges}</strong>
                      </div>
                    </div>
                    <div className="col-md-3 mb-2">
                      <div className="card bg-light p-2 text-center">
                        <small className="text-muted">EMI/Month</small>
                        <strong className="text-primary">₹{financeSummary.emiPerMonth}</strong>
                      </div>
                    </div>
                    <div className="col-md-3 mb-2">
                      <div className="card bg-light p-2 text-center">
                        <small className="text-muted">Total EMI</small>
                        <strong>₹{financeSummary.totalEmi}</strong>
                      </div>
                    </div>
                    <div className="col-md-3 mb-2">
                      <div className="card bg-light p-2 text-center">
                        <small className="text-muted">Total Interest</small>
                        <strong>₹{financeSummary.totalInterest}</strong>
                      </div>
                    </div>
                    <div className="col-md-3 mb-2">
                      <div className="card bg-light p-2 text-center">
                        <small className="text-muted">Total PF + GST</small>
                        <strong>₹{financeSummary.totalPfGst}</strong>
                      </div>
                    </div>
                    <div className="col-md-3 mb-2">
                      <div className="card bg-light p-2 text-center">
                        <small className="text-muted">Total Principal</small>
                        <strong>₹{financeSummary.totalPrincipal}</strong>
                      </div>
                    </div>
                    <div className="col-md-9 mb-2">
                      <div className="card bg-success bg-opacity-10 p-2 text-center">
                        <small className="text-muted">Total Payable</small>
                        <strong className="text-success fs-5">₹{financeSummary.totalPayable}</strong>
                      </div>
                    </div>
                  </div>
                )}

                {/* EMI Schedule Table */}
                <table className="table table-bordered table-hover">

                  <thead className="table-dark">
                    <tr>
                      <th>Month</th>
                      <th>EMI Date</th>
                      <th>EMI</th>
                      <th>Principal</th>
                      <th>Interest</th>
                      <th>PF + GST</th>
                      <th>Balance</th>
                    </tr>
                  </thead>

                  <tbody>

                    {financeEmiList.map((emi, index) => (
                      <tr key={index}>
                        <td>{emi.month}</td>
                        <td>{emi.emiDate}</td>
                        <td className="fw-bold">₹{emi.emi}</td>
                        <td>₹{emi.principal}</td>
                        <td>₹{emi.interest}</td>
                        <td>₹{emi.pfGst}</td>
                        <td>₹{emi.balance}</td>
                      </tr>
                    ))}

                  </tbody>

                </table>

              </div>

              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowFinanceModal(false)}
                >
                  Close
                </button>
              </div>

            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default EmiCalculator;
